import { Input as InputPrimitive } from '@base-ui/react/input';
import * as React from 'react';
import {
  type FieldProps,
  FieldTooltip,
  hasFieldProps,
  splitFieldProps,
} from '@/components/atoms/forms/_shared';
import {
  Field,
  FieldBottom,
  FieldBottomRight,
  FieldControl,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldTop,
  FieldTopRight,
} from '@/components/ui/form-field';
import { GradientBorder } from '@/components/ui/gradient-border';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

export type InputSize = 'xs' | 'sm' | 'md' | 'lg';
export type InputShape = 'rounded' | 'round';
export type InputState = 'default' | 'success' | 'destructive';

/**
 * Input renders a text input. Pass field props (label, error, description, etc.)
 * to automatically wrap in a Field with label, validation, and helper text.
 * Without field props, renders a bare input with zero overhead.
 *
 * @example
 * ```tsx
 * // Bare input
 * <Input placeholder="Search..." />
 *
 * // With field props — auto-wraps in Field
 * <Input label="Email" error={errors.email} placeholder="name@example.com" />
 *
 * // With icons
 * <Input startIcon={<SearchIcon />} placeholder="Search..." />
 *
 * // Compound Field composition (escape hatch)
 * <Field>
 *   <Field.Label>Email</Field.Label>
 *   <Field.Control>{(props) => <Input {...props} type="email" />}</Field.Control>
 *   <Field.Error />
 * </Field>
 * ```
 */
interface InputControlProps
  extends Omit<React.ComponentProps<'input'>, 'size'> {
  /** Input size variant */
  size?: InputSize;
  /** Input shape variant */
  shape?: InputShape;
  /** Input state for validation feedback */
  state?: InputState;
  /** Icon displayed at the start (inline-start) of the input - respects RTL */
  startIcon?: React.ReactNode;
  /** Icon displayed at the end (inline-end) of the input - respects RTL */
  endIcon?: React.ReactNode;
  /** Addon content displayed at the start (inline-start) outside the input - respects RTL */
  startAddon?: React.ReactNode;
  /** Addon content displayed at the end (inline-end) outside the input - respects RTL */
  endAddon?: React.ReactNode;
  /** Whether to show an outline/border variant */
  hasOutline?: boolean;
  /** Visual appearance variant. 'ai' wraps the input in an animated gradient border. */
  appearance?: 'default' | 'ai';
  /** Explicitly enables animated gradient border (only for appearance="ai"). */
  animated?: boolean;
  /** Shows a localized loading spinner (only for appearance="ai"). */
  loading?: boolean;
}

export interface InputProps extends InputControlProps, FieldProps {}

const sizeClasses: Record<InputSize, string> = {
  xs: 'h-[26px] px-3 py-0.5 text-sm',
  sm: 'h-8 px-3 py-1 text-sm',
  md: 'h-9 px-3 py-1 text-sm',
  lg: 'h-10 px-3 py-1 text-sm',
};

const shapeClasses: Record<InputShape, string> = {
  rounded: 'rounded-xl',
  round: 'rounded-full',
};

const stateClasses: Record<InputState, string> = {
  default: '',
  success: 'border-success',
  destructive: 'border-destructive',
};

const iconSizeClasses: Record<InputSize, string> = {
  xs: "[&>svg]:size-4 [&_svg:not([class*='size-'])]:size-4",
  sm: "[&>svg]:size-4 [&_svg:not([class*='size-'])]:size-4",
  md: "[&>svg]:size-4 [&_svg:not([class*='size-'])]:size-4",
  lg: "[&>svg]:size-4 [&_svg:not([class*='size-'])]:size-4",
};

const iconSlotClasses =
  'absolute top-1/2 inline-flex -translate-y-1/2 items-center justify-center leading-none text-text-quaternary pointer-events-none [&>button]:inline-flex [&>button]:items-center [&>button]:justify-center [&>button]:leading-none [&>svg]:block [&_svg]:shrink-0';

// =============================================================================
// InputControl — bare rendering logic (private)
// =============================================================================

const InputControl = React.forwardRef<HTMLInputElement, InputControlProps>(
  (
    {
      className,
      type,
      state = 'default',
      shape = 'rounded',
      startIcon,
      endIcon,
      startAddon,
      endAddon,
      hasOutline,
      size = 'md',
      disabled,
      appearance = 'default',
      animated,
      loading,
      onFocus,
      onBlur,
      ...props
    },
    ref,
  ) => {
    const isAi = appearance === 'ai';
    const [isFocused, setIsFocused] = React.useState(false);
    const isAnimated = isAi && animated === true;

    const handleFocus = isAi
      ? (e: React.FocusEvent<HTMLInputElement>) => {
          setIsFocused(true);
          onFocus?.(e);
        }
      : onFocus;

    const handleBlur = isAi
      ? (e: React.FocusEvent<HTMLInputElement>) => {
          setIsFocused(false);
          onBlur?.(e);
        }
      : onBlur;

    // AI appearance: simplified input inside GradientBorder
    if (isAi) {
      return (
        <GradientBorder
          animated={isAnimated}
          focused={isFocused}
          disabled={disabled}
          className={cn(
            'p-[2px]! transition-none motion-reduce:animate-none',
            className,
          )}
        >
          <div data-slot="input" className="relative flex items-center w-full">
            <InputPrimitive
              ref={ref}
              type={type}
              disabled={disabled}
              aria-invalid={state === 'destructive' || undefined}
              onFocus={handleFocus}
              onBlur={handleBlur}
              className={cn(
                'w-full bg-transparent outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
                sizeClasses[size],
                'pe-10',
              )}
              {...props}
            />
            <span
              className={cn(
                'absolute end-3 top-1/2 -translate-y-1/2 text-text-quaternary',
                !loading && 'invisible',
              )}
              aria-hidden="true"
            >
              <Spinner
                className={cn(
                  'motion-reduce:animate-none',
                  !loading && 'animate-none',
                )}
              />
            </span>
          </div>
        </GradientBorder>
      );
    }

    const hasAddons = startAddon || endAddon;
    const hasStartIcon = !!startIcon;
    const hasEndIcon = !!endIcon;
    const hasIconsOrAddons = hasStartIcon || hasEndIcon || hasAddons;
    const isDestructive = state === 'destructive';

    // Base input styles
    const inputStyles = cn(
      'bg-custom-background-input-30 border-input focus-visible:border-ring focus-visible:ring-gray-400/50 border motion-safe:transition-[color,border-color,box-shadow] duration-(--duration-fast) ease-standard motion-reduce:transition-none file:h-7 file:text-sm file:font-medium focus-visible:ring-[3px] file:text-foreground placeholder:text-muted-foreground w-full min-w-0 outline-none file:inline-flex file:border-0 file:bg-transparent disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-muted read-only:bg-muted read-only:border-border read-only:cursor-default',
      sizeClasses[size],
      shapeClasses[shape],
      stateClasses[state],
      hasOutline &&
        'border-border ring-1 ring-border focus-visible:ring-2 focus-visible:ring-ring',
      hasStartIcon && (size === 'xs' ? 'ps-[30px]' : 'ps-[34px]'),
      hasEndIcon && (size === 'xs' ? 'pe-[30px]' : 'pe-[34px]'),
      hasAddons && startAddon && 'rounded-s-none',
      hasAddons && endAddon && 'rounded-e-none',
    );

    // Simple input without icons/addons - input is the root element
    if (!hasIconsOrAddons) {
      return (
        <InputPrimitive
          ref={ref}
          type={type}
          data-slot="input"
          data-state={state}
          aria-invalid={isDestructive || undefined}
          disabled={disabled}
          onFocus={onFocus}
          onBlur={onBlur}
          className={cn(inputStyles, className)}
          {...props}
        />
      );
    }

    // Input with icons/addons - wrapper div is the root element
    return (
      <div
        data-slot="input"
        data-state={state}
        className={cn('relative flex', className)}
      >
        {startAddon && (
          <span className="inline-flex items-center rounded-s-xl border border-e-0 border-border bg-muted px-3 text-base text-muted-foreground">
            {startAddon}
          </span>
        )}

        <div className="relative flex-1">
          {hasStartIcon && (
            <span
              data-slot="input-start-icon"
              className={cn(iconSlotClasses, 'start-3', iconSizeClasses[size])}
            >
              {startIcon}
            </span>
          )}

          <InputPrimitive
            ref={ref}
            type={type}
            data-slot="input-element"
            data-state={state}
            aria-invalid={isDestructive || undefined}
            disabled={disabled}
            onFocus={onFocus}
            onBlur={onBlur}
            className={inputStyles}
            {...props}
          />

          {hasEndIcon && (
            <span
              data-slot="input-end-icon"
              className={cn(iconSlotClasses, 'end-3', iconSizeClasses[size])}
            >
              {endIcon}
            </span>
          )}
        </div>

        {endAddon && (
          <span className="inline-flex items-center rounded-e-xl border border-s-0 border-border bg-muted px-3 text-base text-muted-foreground">
            {endAddon}
          </span>
        )}
      </div>
    );
  },
);
InputControl.displayName = 'InputControl';

// =============================================================================
// Input — public API with smart field mode
// =============================================================================

const Input = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const [fieldPropsObj, controlProps] = splitFieldProps(props);

  if (!hasFieldProps(fieldPropsObj)) {
    return (
      <InputControl
        ref={ref}
        {...(controlProps as InputControlProps)}
        required={fieldPropsObj.required}
      />
    );
  }

  const {
    label,
    required,
    labelIcon,
    tooltip,
    topRight,
    description,
    bottomRight,
    error,
    fieldClassName,
  } = fieldPropsObj;
  const cp = controlProps as InputControlProps;
  const resolvedState = error ? 'destructive' : cp.state;
  const hasTopRow = label || topRight;
  const hasBottomRow = description || bottomRight || error;

  return (
    <Field
      state={resolvedState}
      disabled={cp.disabled}
      className={fieldClassName}
    >
      {hasTopRow && (
        <FieldTop>
          {label && (
            <FieldLabel required={required} labelIcon={labelIcon}>
              {label}
              {tooltip && <FieldTooltip content={tooltip} />}
            </FieldLabel>
          )}
          {topRight && <FieldTopRight>{topRight}</FieldTopRight>}
        </FieldTop>
      )}
      <FieldControl>
        {(ctrlProps) => (
          <InputControl
            ref={ref}
            {...cp}
            {...ctrlProps}
            required={required}
            state={resolvedState}
          />
        )}
      </FieldControl>
      {hasBottomRow && (
        <FieldBottom>
          {error && <FieldError>{error}</FieldError>}
          {!error && description && (
            <FieldDescription>{description}</FieldDescription>
          )}
          {bottomRight && (
            <FieldBottomRight className="ms-auto">
              {bottomRight}
            </FieldBottomRight>
          )}
        </FieldBottom>
      )}
    </Field>
  );
});
Input.displayName = 'Input';

export {
  Input,
  iconSizeClasses as inputIconSizeClasses,
  shapeClasses as inputShapeClasses,
  sizeClasses as inputSizeClasses,
  stateClasses as inputStateClasses,
};
